import * as React from 'react';
import { useLayout } from '../../contexts/LayoutContext';
import { ResizableDivider } from '../ResizableDivider';

const MAX_BOTTOM_HEIGHT_RATIO = 0.6; // 60% of MainPane height

export interface MainPaneProps {
  children: React.ReactNode;
  bottomPaneContent?: React.ReactNode;
}

/**
 * MainPane - primary content region
 *
 * Behavior (frontend-architecture.md §4, requirements):
 * - occupies central workspace
 * - contains primary working area
 * - supports BottomPane with horizontal divider
 * - BottomPane hidden by default
 * - BottomPane reusable for logs, activity, console, detail views
 * - when hidden, BottomPane must not reserve height
 */
export function MainPane({ children, bottomPaneContent }: MainPaneProps) {
  const { bottomPaneVisible, bottomPaneHeight, setBottomPaneHeight } = useLayout();
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Read min height from CSS token (design-system.md: all visual decisions from tokens)
  const minBottomHeight = React.useMemo(() => {
    const root = document.documentElement;
    return parseInt(getComputedStyle(root).getPropertyValue('--pane-bottom-height-min'), 10);
  }, []);

  const handleResize = React.useCallback(
    (delta: number) => {
      if (!containerRef.current) return;

      const containerHeight = containerRef.current.clientHeight;
      const maxHeight = containerHeight * MAX_BOTTOM_HEIGHT_RATIO;
      const newHeight = Math.max(minBottomHeight, Math.min(maxHeight, bottomPaneHeight - delta));

      setBottomPaneHeight(newHeight);
    },
    [bottomPaneHeight, setBottomPaneHeight, minBottomHeight]
  );

  return (
    <main className="shell-mainpane" ref={containerRef}>
      <div className="shell-mainpane-content">{children}</div>

      {bottomPaneVisible && bottomPaneContent && (
        <>
          <ResizableDivider
            direction="horizontal"
            onResize={handleResize}
            ariaLabel="Resize bottom pane"
          />
          <div className="shell-mainpane-bottom" style={{ height: `${bottomPaneHeight}px` }}>
            {bottomPaneContent}
          </div>
        </>
      )}
    </main>
  );
}
